import { ANNOTATION_SCHEMAS } from "../config/annotationSchemas";

/**
 * Return the canonical category carried by a model segmentation condition.
 * Conditions use `organ/category`; keeping this extraction here prevents
 * annotation persistence and editing from drifting to different labels.
 */
export function segmentationCategory(condition: string): string {
  const [, category] = condition.split("/");
  return category || condition;
}

/** Project labels drive both study arms; schemas remain the form/persistence contract. */
export function segmentationConditionLabels(conditions: readonly string[]): Record<string, string> {
  const labels: Record<string, string> = {};
  for (const condition of conditions) {
    const category = segmentationCategory(condition);
    if (labels[category] && labels[category] !== condition) {
      throw new Error(`Ambiguous segmentation conditions for category ${category}`);
    }
    labels[category] = condition;
  }
  return labels;
}

export function formatSegmentationCondition(condition: string): string {
  return (condition.split("/").at(-1) ?? condition)
    .split("_")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * The persisted-annotation category for a model condition, or null when the
 * category has no schema in the persisted vocabulary. Accept/Edit must only be
 * offered when this is non-null: the backend's annotation validator rejects
 * unknown categories (422), and the editor cannot render a form (Save stays
 * disabled). A condition can therefore render as a mask without acquiring
 * persistence affordances it cannot honor.
 */
export function persistedSegmentationCategory(condition: string): string | null {
  const category = segmentationCategory(condition);
  return category in ANNOTATION_SCHEMAS ? category : null;
}
